import React, { useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { Save, Loader2, ClipboardList, Lock, Database } from "lucide-react";
import { toast } from "sonner";

const C = { Sailing: "#2E86DE", Spot: "#F39C12", SWGP: "#9AA5B1" };
const tip = { contentStyle: { background: "hsl(var(--popover))", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, fontSize: 12 } };
const sum = (o) => Object.values(o).reduce((a, b) => a + (Number(b) || 0), 0);

function Grid({ title, keys, statuses, values, onChange, editable, testid }) {
  const total = keys.reduce((a, k) => a + sum(values[k] || {}), 0);
  return (
    <Card className="gold-ring glass overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[hsl(var(--primary))]/90 hover:bg-[hsl(var(--primary))]/90">
              <TableHead className="font-700 text-[hsl(var(--primary-foreground))]">{title}</TableHead>
              {statuses.map((s) => <TableHead key={s} className="text-right font-700 text-[hsl(var(--primary-foreground))]">{s}</TableHead>)}
              <TableHead className="text-right font-700 text-[hsl(var(--primary-foreground))]">Total</TableHead>
              <TableHead className="text-right font-700 text-[hsl(var(--primary-foreground))]">%</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.map((k) => { const row = values[k] || {}; const t = sum(row); return (
              <TableRow key={k} data-testid={`${testid}-row-${k}`}>
                <TableCell className="font-600">{k}</TableCell>
                {statuses.map((s) => <TableCell key={s} className="text-right">{editable ? <Input type="number" min={0} value={row[s] ?? 0} onChange={(e) => onChange(k, s, e.target.value)} className="ml-auto h-8 w-20 text-right font-mono-data" data-testid={`${testid}-${k}-${s}`} /> : <span className="font-mono-data">{row[s] ?? 0}</span>}</TableCell>)}
                <TableCell className="text-right font-mono-data font-700" data-testid={`${testid}-${k}-total`}>{t}</TableCell>
                <TableCell className="text-right font-mono-data text-muted-foreground">{total ? `${Math.round((t * 1000) / total) / 10}%` : "0%"}</TableCell>
              </TableRow>); })}
            <TableRow className="bg-secondary/60 font-700 hover:bg-secondary/60" data-testid={`${testid}-total`}>
              <TableCell>Total</TableCell>
              {statuses.map((s) => <TableCell key={s} className="text-right font-mono-data">{keys.reduce((a, k) => a + (Number(values[k]?.[s]) || 0), 0)}</TableCell>)}
              <TableCell className="text-right font-mono-data">{total}</TableCell>
              <TableCell className="text-right font-mono-data">{total ? "100%" : "0%"}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

// Officer-editable official census (totals grid) — charts redraw live as the numbers change
export default function OfficialCensus({ data, officer, onSaved }) {
  const [des, setDes] = useState(data.official.designations);
  const [fri, setFri] = useState(data.official.frigates);
  const [date, setDate] = useState(data.official.report_date || "");
  const [source, setSource] = useState(data.source);
  const [busy, setBusy] = useState(false);
  const set = (setter) => (k, s, v) => setter((p) => ({ ...p, [k]: { ...p[k], [s]: v === "" ? "" : Math.max(0, parseInt(v, 10) || 0) } }));

  const byStatus = useMemo(() => data.statuses.map((s) => ({ name: s, value: data.designations.reduce((a, d) => a + (Number(des[d]?.[s]) || 0), 0) })).filter((x) => x.value > 0), [des, data]);
  const frigateRows = useMemo(() => data.frigates.map((f) => ({ label: f, ...Object.fromEntries(data.statuses.map((s) => [s, Number(fri[f]?.[s]) || 0])) })), [fri, data]);
  const desTotal = data.designations.reduce((a, d) => a + sum(des[d] || {}), 0);
  const friTotal = data.frigates.reduce((a, f) => a + sum(fri[f] || {}), 0);

  const save = async () => {
    setBusy(true);
    try { const { data: d } = await api.put("/population/official", { designations: des, frigates: fri, report_date: date || null, source }); toast.success("Official census saved — dashboard updated"); onSaved(d); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="official-census">
      <Card className="gold-ring glass flex flex-wrap items-center gap-4 p-4">
        <div className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-[hsl(var(--gold))]" /><h3 className="font-display text-lg font-700">Official census</h3></div>
        <p className="text-xs text-muted-foreground">Totals from the Galleon's membership record. {officer ? "Edit the numbers — charts and the Mess card update automatically." : "Officers keep these figures current."}</p>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {desTotal !== friTotal && <Badge variant="outline" className="border-amber-500/50 text-amber-500" data-testid="census-mismatch">Designations {desTotal} ≠ Frigates {friTotal}</Badge>}
          {officer ? (
            <>
              <div><Label className="text-[0.65rem]">Report date</Label><Input type="date" value={date?.slice(0, 10) || ""} onChange={(e) => setDate(e.target.value)} className="mt-0.5 h-8 w-40" data-testid="census-date-input" /></div>
              <div><Label className="text-[0.65rem]">Dashboard shows</Label>
                <div className="mt-0.5 flex rounded-md border border-border p-0.5" data-testid="census-source-toggle">
                  {[["official", "Official totals"], ["roster", "Live roster"]].map(([v, l]) => <button key={v} type="button" onClick={() => setSource(v)} className={`rounded px-2.5 py-1 text-xs font-600 ${source === v ? "bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]" : "text-muted-foreground"}`} data-testid={`census-source-${v}`}>{l}</button>)}
                </div></div>
              <Button onClick={save} disabled={busy} className="bg-[hsl(var(--gold))] font-700 text-[hsl(var(--primary-foreground))]" data-testid="census-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" />Save census</>}</Button>
            </>
          ) : <Badge variant="outline" className="gap-1 text-muted-foreground"><Lock className="h-3 w-3" />Officers edit</Badge>}
        </div>
      </Card>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <div className="space-y-5 lg:col-span-3">
          <Grid title="Designation" keys={data.designations} statuses={data.statuses} values={des} onChange={set(setDes)} editable={officer} testid="census-designation" />
          <Grid title="Frigate" keys={data.frigates} statuses={data.statuses} values={fri} onChange={set(setFri)} editable={officer} testid="census-frigate" />
        </div>
        <div className="space-y-5 lg:col-span-2">
          <Card className="gold-ring glass p-4" data-testid="census-pie">
            <p className="text-center text-sm font-700">Status Mix · {desTotal} members</p>
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byStatus} dataKey="value" nameKey="name" innerRadius="52%" outerRadius="80%" paddingAngle={2} stroke="none" isAnimationActive>{byStatus.map((m) => <Cell key={m.name} fill={C[m.name]} />)}</Pie>
                  <Tooltip {...tip} /><Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card className="gold-ring glass p-4" data-testid="census-frigate-chart">
            <p className="text-center text-sm font-700">Frigate by Status</p>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={frigateRows} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" /><XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip {...tip} /><Legend wrapperStyle={{ fontSize: 11 }} />
                  {data.statuses.map((s) => <Bar key={s} dataKey={s} stackId="a" fill={C[s]} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <p className="flex items-center gap-1.5 text-[0.65rem] text-muted-foreground"><Database className="h-3 w-3" />Last updated {data.official.updated_at ? new Date(data.official.updated_at).toLocaleString() : "—"}{data.official.updated_by ? ` · ${data.official.updated_by}` : ""}</p>
        </div>
      </div>
    </div>
  );
}
